import { useMemo } from 'react';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { selectors as serviceLinksSelectors } from '@rover/react-lib/src/redux/ducks/serviceLinks.duck';
import {
  ApiServiceLinksRetrieve200DataItem,
  useApiServiceLinksRetrieve,
} from '@rover/rsdk/src/apiClient/latest';
import {
  CountryUrlPrefix,
  getCountryUrlPrefix,
  US_COUNTRY_URL_PREFIX,
} from '@rover/shared/js/constants/roverGeo.constants';
import {
  DISPLAY_NAME_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import { getPath } from '../utils/getFullUrl';

import useCountryConfigurationShim from './useCountryConfigurationShim';
import useDucks from './useDucks';

export type ServiceLinksData = {
  serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null | undefined;
  serviceLinksForCurrentRoute: ServiceLinks | null;
  serviceLinksWithDisplayName: ServiceLinksItemWithDisplayName[] | null;
};

export type ServiceLinks = Record<ServiceLandingEnum, string>;
export type ServiceLinksByCountry = Record<CountryUrlPrefix, ServiceLinks>;
export type ServiceLinksItemWithDisplayName = ApiServiceLinksRetrieve200DataItem & {
  name: string;
};

const selector = (
  state
): { serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null | undefined } => ({
  serviceLinks: serviceLinksSelectors.getServiceLinks(state),
});

const getServiceLinksGroupedByCountry = (
  serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null | undefined
): ServiceLinksByCountry | null => {
  if (!serviceLinks) return null;
  const serviceLinksByCountry = {} as ServiceLinksByCountry;

  serviceLinks.forEach(({ type, href }) => {
    const countryCode = getCountryUrlPrefix(href) ?? US_COUNTRY_URL_PREFIX;

    if (!(countryCode in serviceLinksByCountry)) {
      serviceLinksByCountry[countryCode] = {} as ServiceLinksByCountry[CountryUrlPrefix];
    }

    if (type) {
      serviceLinksByCountry[countryCode][type] = href;
    }
  });

  return serviceLinksByCountry;
};

const getServiceLinksByCountry = (
  serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null | undefined,
  countryUrlPrefix: string
): ServiceLinks | null => {
  const serviceLinksGroupedByCountry = getServiceLinksGroupedByCountry(serviceLinks);
  if (!serviceLinksGroupedByCountry) return null;
  return serviceLinksGroupedByCountry[countryUrlPrefix ?? US_COUNTRY_URL_PREFIX] ?? null;
};

const getServiceLinksForCurrentRoute = (
  serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null | undefined,
  countryUrlPrefix: string
): ServiceLinks | null => {
  return getServiceLinksByCountry(serviceLinks, countryUrlPrefix);
};

const getServiceLinksWithDisplayName = (
  serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null | undefined
): ServiceLinksItemWithDisplayName[] | null => {
  if (!serviceLinks) return null;
  return serviceLinks.map(({ href, type }: ApiServiceLinksRetrieve200DataItem) => ({
    type,
    href,
    name: DISPLAY_NAME_FOR_SERVICE_LANDING_ENUM()[type!],
  }));
};

const useServiceLinksShim = (): ServiceLinksData => {
  const { isReactQueryRoute } = usePageContext();
  const { data: selectedData } = useDucks({ selector });
  const { data } = useApiServiceLinksRetrieve({ query: { enabled: isReactQueryRoute } });
  const serviceLinks = isReactQueryRoute ? data?.data : selectedData.serviceLinks;
  const { navigation } = useCountryConfigurationShim();
  const navigationUrlPrefix = navigation?.urlPrefix;
  const path = getPath();

  const countryUrlPrefix =
    navigationUrlPrefix ?? getCountryUrlPrefix(path) ?? US_COUNTRY_URL_PREFIX;

  const serviceLinksForCurrentRoute = useMemo(
    () => getServiceLinksForCurrentRoute(serviceLinks, countryUrlPrefix),
    [serviceLinks, countryUrlPrefix]
  );

  const serviceLinksWithDisplayName = useMemo(
    () => getServiceLinksWithDisplayName(serviceLinks),
    [serviceLinks]
  );

  return { serviceLinks, serviceLinksForCurrentRoute, serviceLinksWithDisplayName };
};

export default useServiceLinksShim;
